package dev.lingvo.shared.screenshots.doc

import dev.lingvo.shared.StoreLimits
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.BAD_ASSET_ID
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.BAD_COLOR
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.BAD_ID
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.BAD_SLOT_REF
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.BAD_STOPS
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.BAD_TEXT
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.FLOOR_ABOVE_MAX
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.NOT_FINITE
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.OUT_OF_RANGE
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.STRING_TOO_LONG
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.UNKNOWN_FONT
import kotlin.math.abs
import kotlin.math.min

/**
 * Collects issues while [DesignValidator] walks a document, with one checker per value type.
 *
 * Every `Double` in the model passes through [num], which reports NOT_FINITE before any range check: `1e400`
 * decodes to Infinity and NaN fails every comparison, so a range check alone would let both through. It also
 * refuses `-0.0` and non-zero magnitudes below [DesignLimits.MIN_MAGNITUDE]: the stored form spells both as `0`,
 * so allowing them would break `decode(encode(d)) == d`. Every free-text string passes through [text], which
 * measures code points and refuses invisible control and direction characters.
 */
internal class IssueSink(private val fonts: FontNameRule) {
    val issues = ArrayList<DesignIssue>()

    fun add(code: DesignIssueCode, path: String, message: String) {
        issues.add(DesignIssue(code, path, message))
    }

    /** Finite and within [min]..[max]; [minExclusive] makes the lower bound strict. Returns true when valid. */
    fun num(path: String, value: Double, min: Double, max: Double, minExclusive: Boolean = false): Boolean {
        if (!value.isFinite()) {
            add(NOT_FINITE, path, "Numbers must be finite.")
            return false
        }
        if (value == 0.0 && 1.0 / value < 0) {
            add(OUT_OF_RANGE, path, "Write 0, not -0.")
            return false
        }
        if (value != 0.0 && abs(value) < DesignLimits.MIN_MAGNITUDE) {
            add(OUT_OF_RANGE, path, "Numbers this close to zero are not allowed; write 0.")
            return false
        }
        val belowMin = if (minExclusive) value <= min else value < min
        if (belowMin || value > max) {
            val low = if (minExclusive) "above ${plain(min)}" else "at least ${plain(min)}"
            add(OUT_OF_RANGE, path, "Must be $low and at most ${plain(max)}; found ${plain(value)}.")
            return false
        }
        return true
    }

    fun int(path: String, value: Int, min: Int, max: Int, code: DesignIssueCode = OUT_OF_RANGE): Boolean {
        if (value < min || value > max) {
            add(code, path, "Must be from $min to $max; found $value.")
            return false
        }
        return true
    }

    fun count(path: String, size: Int, min: Int, max: Int, code: DesignIssueCode, what: String) {
        if (size < min || size > max) add(code, path, "Needs $min to $max $what; found $size.")
    }

    fun id(path: String, value: String) {
        if (!DesignLimits.ID_REGEX.matches(value)) {
            add(BAD_ID, path, "Ids are 1 to 40 letters, digits, '_' or '-'; found '${value.take(40)}'.")
        }
    }

    fun slotKey(path: String, value: String) {
        if (!DesignLimits.SLOT_KEY_REGEX.matches(value)) {
            add(BAD_ID, path, "Slot keys are 1 to 48 lowercase letters, digits, '_' or '-', starting with a letter or digit.")
        }
    }

    /** Free text: at most [max] code points, and no character that is invisible or reorders what is shown. */
    fun text(path: String, value: String, max: Int) {
        val length = StoreLimits.codePointLength(value)
        if (length > max) add(STRING_TOO_LONG, path, "At most $max characters; found $length.")
        badCharacter(value)?.let { code ->
            val hex = code.toString(16).uppercase().padStart(4, '0')
            add(BAD_TEXT, path, "Text may not contain control, direction-override or unpaired surrogate characters (U+$hex).")
        }
    }

    /** Lists with a count limit: when the count fails, the items are not checked one by one. */
    fun <T> bounded(path: String, items: List<T>, max: Int, what: String, code: DesignIssueCode = OUT_OF_RANGE, each: (Int, T) -> Unit) {
        if (items.size > max) {
            add(code, path, "At most $max $what; found ${items.size}.")
            return
        }
        items.forEachIndexed(each)
    }

    fun color(path: String, ref: String) {
        if (!ColorRefs.isValid(ref)) add(BAD_COLOR, path, "Not a colour: '${ref.take(24)}'.")
    }

    fun hex(path: String, value: String, opaque: Boolean) {
        val ok = if (opaque) ColorRefs.isOpaqueHex(value) else ColorRefs.isHex(value)
        if (!ok) add(BAD_COLOR, path, "Expected ${if (opaque) "#RRGGBB" else "#RRGGBB or #RRGGBBAA"}; found '${value.take(24)}'.")
    }

    fun font(path: String, family: String?) {
        if (family != null && !fonts.knows(family)) add(UNKNOWN_FONT, path, "Unknown font family '${family.take(40)}'.")
    }

    fun assetId(path: String, value: String) {
        if (!DesignLimits.ASSET_ID_REGEX.matches(value)) {
            add(BAD_ASSET_ID, path, "Asset ids are lowercase letters, digits and '-', at most 64; found '${value.take(40)}'.")
        }
    }

    fun box(path: String, box: Box) {
        num("$path/x", box.x, -DesignLimits.COORD_LIMIT, DesignLimits.COORD_LIMIT)
        num("$path/y", box.y, -DesignLimits.COORD_LIMIT, DesignLimits.COORD_LIMIT)
        num("$path/w", box.w, DesignLimits.MIN_EXTENT, DesignLimits.COORD_LIMIT)
        num("$path/h", box.h, DesignLimits.MIN_EXTENT, DesignLimits.COORD_LIMIT)
        num("$path/rotation", box.rotation, -DesignLimits.ROTATION_LIMIT, DesignLimits.ROTATION_LIMIT)
    }

    /** Corner radii are limited to half the shorter side of the layer's box. */
    fun cornerRadius(path: String, radius: Double, box: Box) {
        val half = min(box.w, box.h) / 2
        val max = if (half.isFinite() && half > 0) half else DesignLimits.COORD_LIMIT
        num(path, radius, 0.0, max)
    }

    fun shadow(path: String, shadow: Shadow) {
        num("$path/dx", shadow.dx, -DesignLimits.MAX_SHADOW_OFFSET, DesignLimits.MAX_SHADOW_OFFSET)
        num("$path/dy", shadow.dy, -DesignLimits.MAX_SHADOW_OFFSET, DesignLimits.MAX_SHADOW_OFFSET)
        num("$path/blur", shadow.blur, 0.0, DesignLimits.MAX_BLUR)
        color("$path/color", shadow.color)
    }

    fun paint(path: String, paint: Paint) {
        when (paint) {
            is Paint.Solid -> color("$path/color", paint.color)
            is Paint.Linear -> {
                num("$path/angle", paint.angle, -DesignLimits.ROTATION_LIMIT, DesignLimits.ROTATION_LIMIT)
                stops("$path/stops", paint.stops)
            }
            is Paint.Radial -> {
                num("$path/cx", paint.cx, -1.0, 2.0)
                num("$path/cy", paint.cy, -1.0, 2.0)
                num("$path/r", paint.r, 0.0, 4.0, minExclusive = true)
                stops("$path/stops", paint.stops)
            }
        }
    }

    private fun stops(path: String, stops: List<Stop>) {
        if (stops.size < DesignLimits.MIN_STOPS || stops.size > DesignLimits.MAX_STOPS) {
            add(BAD_STOPS, path, "A gradient needs ${DesignLimits.MIN_STOPS} to ${DesignLimits.MAX_STOPS} stops; found ${stops.size}.")
            if (stops.size > DesignLimits.MAX_STOPS) return
        }
        var previous = Double.NEGATIVE_INFINITY
        stops.forEachIndexed { index, stop ->
            val at = "$path/$index/at"
            if (!num(at, stop.at, Double.NEGATIVE_INFINITY, Double.POSITIVE_INFINITY)) {
                Unit
            } else if (stop.at < 0.0 || stop.at > 1.0 || stop.at < previous) {
                add(BAD_STOPS, at, "Stops run from 0 to 1 and never go backwards; found ${plain(stop.at)}.")
            } else {
                previous = stop.at
            }
            color("$path/$index/color", stop.color)
        }
    }

    fun stroke(path: String, stroke: StrokeSpec) {
        num("$path/width", stroke.width, 0.0, DesignLimits.MAX_STROKE_WIDTH)
        paint("$path/paint", stroke.paint)
        var allValid = true
        bounded("$path/dash", stroke.dash, DesignLimits.MAX_DASH, "dash lengths") { index, length ->
            if (!num("$path/dash/$index", length, DesignLimits.MIN_EXTENT, DesignLimits.COORD_LIMIT)) allValid = false
        }
        if (allValid && stroke.dash.size in 1..DesignLimits.MAX_DASH && stroke.dash.sum() < DesignLimits.MIN_DASH_PATTERN) {
            add(OUT_OF_RANGE, "$path/dash", "A dash pattern is at least ${plain(DesignLimits.MIN_DASH_PATTERN)} long.")
        }
    }

    fun fit(path: String, fit: FitSpec) {
        val maxOk = num("$path/maxSize", fit.maxSize, DesignLimits.MIN_FONT_SIZE, DesignLimits.MAX_FONT_SIZE)
        val floorOk = num("$path/floorSize", fit.floorSize, DesignLimits.MIN_FONT_SIZE, DesignLimits.MAX_FONT_SIZE)
        int("$path/maxLines", fit.maxLines, 1, DesignLimits.MAX_LINES)
        if (maxOk && floorOk && fit.floorSize > fit.maxSize) {
            add(FLOOR_ABOVE_MAX, "$path/floorSize", "The floor size ${plain(fit.floorSize)} is above the maximum ${plain(fit.maxSize)}.")
        }
    }

    fun weight(path: String, weight: Int) {
        if (weight < 100 || weight > 900 || weight % 100 != 0) {
            add(OUT_OF_RANGE, path, "Weights are 100 to 900 in steps of 100; found $weight.")
        }
    }

    fun lineHeight(path: String, value: Double) {
        num(path, value, DesignLimits.MIN_LINE_HEIGHT, DesignLimits.MAX_LINE_HEIGHT)
    }

    fun letterSpacing(path: String, value: Double) {
        num(path, value, DesignLimits.MIN_LETTER_SPACING, DesignLimits.MAX_LETTER_SPACING)
    }

    fun style(path: String, style: TextStyle) {
        font("$path/family", style.family)
        weight("$path/weight", style.weight)
        paint("$path/color", style.color)
        lineHeight("$path/lineHeight", style.lineHeight)
        letterSpacing("$path/letterSpacingEm", style.letterSpacingEm)
    }

    fun imageSource(path: String, source: ImageSource, slots: Int) {
        when (source) {
            is ImageSource.Capture -> {
                slot("$path/slot", source.slot, slots)
                int("$path/planOffset", source.planOffset, -DesignLimits.MAX_PLAN_OFFSET, DesignLimits.MAX_PLAN_OFFSET, BAD_SLOT_REF)
            }
            is ImageSource.Asset -> assetId("$path/assetId", source.assetId)
            ImageSource.BrandLogo -> Unit
        }
    }

    fun slot(path: String, slot: Int, slots: Int) {
        if (slot < 0 || slot >= slots) add(BAD_SLOT_REF, path, "Slot $slot is not on this page (it has $slots).")
    }

    fun crop(path: String, crop: NRect) {
        val ok = listOf(
            num("$path/x", crop.x, 0.0, 1.0),
            num("$path/y", crop.y, 0.0, 1.0),
            num("$path/w", crop.w, DesignLimits.MIN_CROP, 1.0),
            num("$path/h", crop.h, DesignLimits.MIN_CROP, 1.0),
        ).all { it }
        if (ok && (crop.x + crop.w > 1.0 + EPSILON || crop.y + crop.h > 1.0 + EPSILON)) {
            add(OUT_OF_RANGE, path, "A crop must stay inside the image.")
        }
    }

    private companion object {
        const val EPSILON = 1e-9

        /** Inspect UTF-16 units explicitly on both backends; a valid pair consumes both units. */
        fun badCharacter(value: String): Int? {
            var index = 0
            while (index < value.length) {
                val code = value[index].code
                when {
                    code in 0xD800..0xDBFF && index + 1 < value.length && value[index + 1].code in 0xDC00..0xDFFF -> index++
                    code in 0xD800..0xDFFF -> return code
                    code < 0x20 || code in 0x7F..0x9F -> return code
                    code == 0x2028 || code == 0x2029 || code in 0x202A..0x202E || code in 0x2066..0x2069 -> return code
                }
                index++
            }
            return null
        }

        /** Numbers in messages, the same on every platform: integers without ".0". */
        fun plain(value: Double): String =
            if (value.isFinite() && value == kotlin.math.floor(value) && kotlin.math.abs(value) < 1e15) {
                value.toLong().toString()
            } else {
                value.toString()
            }
    }
}
